import {
  Currency,
  ImpactShopProductType,
  ImpactShopSortOption,
  ImpactType,
  PROJECT_TYPES_BY_IMPACT_TYPE,
  User,
  isBaftaUser,
  isImpactFundProductType,
  IMPACT_FUND_MINIMUM_QUANTITY,
  values,
} from "@ecologi/shared";
import React, { useState } from "react";
import { useRouter } from "next/router";
import { ProjectType } from "@prisma/client";
import { animateScroll as scroller } from "react-scroll";
import { Button } from "../Button";
import { ImpactShopProductCard } from "../ImpactShopProductCard/ImpactShopProductCard";
import { UnsureWhatToFundCard } from "../ImpactShopProductCard/UnsureWhatToFundCard";
import { UnsureWhatToFundModal } from "../UnsureWhatToFundModal/UnsureWhatToFundModal";
import styled from "@emotion/styled";
import { CARD_WIDTH } from "../ImpactShopProductCard/ImpactShopProductCard.styles";
import { ImpactShopFilterBar } from "./ImpactShopFilterBar";
import {
  useImpactShopFilter,
  ImpactShopFilterType,
} from "./useImpactShopFilter";
import {
  CategoryFilterValue,
  IMPACT_FUND_VALUE,
  ALL_LOCATIONS_VALUE,
  ALL_CARBON_VALUE,
} from "./impactShopFilters.constants";

const BottomSectionContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0 48px;
`;

const ImpactShopContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  padding: 0 16px;
  background-color: ${({ theme }) => theme.colors.mist};

  @media ${({ theme }) => theme.bp.tabletUp} {
    grid-template-columns: repeat(auto-fill, minmax(${CARD_WIDTH}px, 1fr));
  }

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    grid-gap: 24px;
    padding: 0 24px;
  }
`;

const handleBackToTop = () => {
  scroller.scrollToTop();
};

export enum FILTER_TYPE_ANALYTICS {
  SORT = "sort",
  CATEGORY = "category",
  LOCATION = "location",
}

export type ImpactShopHomeContentProps = {
  currency: Currency;
  products: ImpactShopProductType[];
  onProductClick?: (title: string) => void;
  onFilterClick: (
    filterType: FILTER_TYPE_ANALYTICS,
    filterValue: string
  ) => void;
  user?: User;
  onAnalyticsEvent?: (
    eventName: string,
    props: Record<string, string | number | boolean>
  ) => void;
};

export const ImpactShopHomeContent = ({
  currency: BASKET_CURRENCY,
  products,
  onProductClick,
  onFilterClick,
  user,
  onAnalyticsEvent,
}: ImpactShopHomeContentProps) => {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    selectedImpactTypes,
    selectedLocations,
    sortOption,
    setSortOption,
    setFilter,
    removeFilter,
    clearAllFilters,
  } = useImpactShopFilter();

  const allLocationSet = new Set<string>();
  products.forEach((product) => {
    product.display.locations?.forEach((location) => {
      allLocationSet.add(location);
    });
  });
  const allLocations = Array.from(allLocationSet).sort();

  const validSelectedLocations = selectedLocations.filter((location) =>
    allLocations.includes(location)
  );

  const availableLocationSet = new Set<string>();
  products.forEach((product) => {
    const matchesCategory =
      selectedImpactTypes.length === 0 ||
      selectedImpactTypes.some((impactType) =>
        productMatchesCategory(product, impactType)
      );

    if (matchesCategory) {
      product.display.locations?.forEach((location) => {
        availableLocationSet.add(location);
      });
    }
  });
  const availableLocations = Array.from(availableLocationSet).sort();

  const availableCategorySet = new Set<CategoryFilterValue>();
  products.forEach((product) => {
    const matchesLocation =
      validSelectedLocations.length === 0 ||
      validSelectedLocations.some((location) =>
        product.display.locations?.includes(location)
      );

    if (matchesLocation) {
      if (isImpactFundProductType(product.productType)) {
        availableCategorySet.add(IMPACT_FUND_VALUE);
      }

      Object.entries(PROJECT_TYPES_BY_IMPACT_TYPE).forEach(
        ([impactType, projectTypes]) => {
          const supportedProjectTypes = projectTypes as readonly ProjectType[];
          if (
            product.display.solutions?.some((solution) =>
              supportedProjectTypes.includes(solution)
            )
          ) {
            availableCategorySet.add(impactType as CategoryFilterValue);
          }
        }
      );
    }
  });
  const availableCategories = Array.from(availableCategorySet);

  const handleCardClick = (slug: string) => () => {
    router.push(`/impact-shop/${slug}`);
    onProductClick?.(slug);
  };

  const handleUnsureCardClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSortChange = (newSortOption: ImpactShopSortOption) => {
    setSortOption(newSortOption);
    onFilterClick(FILTER_TYPE_ANALYTICS.SORT, newSortOption);
  };

  const handleCategoryChange = (value: string) => {
    const newCategories = categoryValueToImpactTypes(value);
    setFilter(ImpactShopFilterType.IMPACT_TYPE, newCategories);
    onFilterClick(FILTER_TYPE_ANALYTICS.CATEGORY, value);
  };

  const handleLocationChange = (value: string) => {
    const newLocations = value === ALL_LOCATIONS_VALUE ? [] : [value];
    setFilter(ImpactShopFilterType.LOCATION, newLocations);
    onFilterClick(FILTER_TYPE_ANALYTICS.LOCATION, value);
  };

  // Pre-filter products to get only visible products with valid prices
  const filteredProducts = products.filter((product) => {
    const price = product.prices.find(
      (p) => p.currency === BASKET_CURRENCY
    )?.amount;
    if (!price) return false;
    return !shouldFilterProduct(
      product,
      selectedImpactTypes,
      validSelectedLocations
    );
  });

  // Add prices to filtered products
  const filteredProductsWithPrice = filteredProducts.map((product) => {
    const price = product.prices.find(
      (p) => p.currency === BASKET_CURRENCY
    )!.amount;

    return {
      ...product,
      price,
      userPrice: isImpactFundProductType(product.productType)
        ? price * IMPACT_FUND_MINIMUM_QUANTITY
        : price,
    };
  });

  // Apply sorting
  const visibleProducts = sortProducts(filteredProductsWithPrice, sortOption);

  // Determine if we should show the UnsureWhatToFundCard
  // Don't show to BAFTA users, but show to everyone else (including unauthenticated)
  const shouldShowUnsureCard = !user || !isBaftaUser(user);

  return (
    <>
      <ImpactShopFilterBar
        selectedImpactTypes={selectedImpactTypes}
        selectedLocations={validSelectedLocations}
        allLocations={allLocations}
        availableLocations={availableLocations}
        availableCategories={availableCategories}
        sortOption={sortOption}
        onCategoryChange={handleCategoryChange}
        onLocationChange={handleLocationChange}
        setSortOption={handleSortChange}
        removeFilter={removeFilter}
        clearAllFilters={clearAllFilters}
      />
      {shouldShowUnsureCard && (
        <UnsureWhatToFundCard
          onClick={handleUnsureCardClick}
          hasSidebar={!!user}
        />
      )}
      <ImpactShopContentContainer>
        {visibleProducts.map((product) => {
          const price = product.prices.find(
            (price) => price.currency === BASKET_CURRENCY
          )!.amount;

          return (
            <ImpactShopProductCard
              badgePath={product.display.badgePath}
              currency={BASKET_CURRENCY}
              description={product.display.snippet || ""}
              image={product.display.images[0]}
              isOutOfStock={product.isOutOfStock}
              key={product.slug}
              onCardClick={handleCardClick(product.slug)}
              price={price}
              tags={product.display.tags}
              title={product.display.title}
              unit={product.display.unitType}
              minQuantity={product.minQuantity}
              productType={product.productType}
            />
          );
        })}
      </ImpactShopContentContainer>
      <BottomSectionContainer>
        <Button
          border
          onClick={handleBackToTop}
          text={"Back to top"}
          textOnly
        />
      </BottomSectionContainer>
      <UnsureWhatToFundModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        products={products}
        currency={BASKET_CURRENCY}
        onAnalyticsEvent={onAnalyticsEvent}
      />
    </>
  );
};

const sortProducts = (
  products: (ImpactShopProductType & { price: number; userPrice: number })[],
  sortOption: ImpactShopSortOption
): (ImpactShopProductType & { price: number; userPrice: number })[] => {
  const inStock = products.filter((p) => !p.isOutOfStock);
  const outOfStock = products.filter((p) => p.isOutOfStock);

  let sortedProducts = [...inStock];

  switch (sortOption) {
    case ImpactShopSortOption.RECOMMENDED:
      sortedProducts.sort(
        (a, b) => a.display.displayOrder - b.display.displayOrder
      );
      break;
    case ImpactShopSortOption.NEWEST:
      sortedProducts.sort((a, b) => {
        const dateComparison =
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        if (dateComparison !== 0) {
          return dateComparison;
        }
        return a.display.displayOrder - b.display.displayOrder;
      });
      break;
    case ImpactShopSortOption.PRICE_LOW_HIGH:
      sortedProducts.sort((a, b) => a.userPrice - b.userPrice);
      break;
    case ImpactShopSortOption.PRICE_HIGH_LOW:
      sortedProducts.sort((a, b) => b.userPrice - a.userPrice);
      break;
  }

  return [...sortedProducts, ...outOfStock];
};

const shouldFilterProduct = (
  product: ImpactShopProductType,
  impactTypes: CategoryFilterValue[],
  locations: string[]
) => {
  if (impactTypes.length > 0) {
    const matchesAnyFilter = impactTypes.some((impactType) =>
      productMatchesCategory(product, impactType)
    );
    if (!matchesAnyFilter) return true;
  }

  if (locations.length > 0) {
    const matchesLocation = locations.some((location) =>
      product.display.locations?.includes(location)
    );
    if (!matchesLocation) return true;
  }

  return false;
};

const productMatchesCategory = (
  product: ImpactShopProductType,
  categoryValue: CategoryFilterValue
): boolean => {
  if (categoryValue === IMPACT_FUND_VALUE) {
    return isImpactFundProductType(product.productType);
  }
  return (
    product.display.solutions?.some((solution) =>
      (
        PROJECT_TYPES_BY_IMPACT_TYPE[
          categoryValue as ImpactType
        ] as readonly ProjectType[]
      ).includes(solution)
    ) ?? false
  );
};

function categoryValueToImpactTypes(value: string): string[] {
  if (value === ALL_CARBON_VALUE) {
    return [ImpactType.CARBON_OFFSETS, ImpactType.CARBON_REMOVAL];
  }
  if (
    value === IMPACT_FUND_VALUE ||
    values(ImpactType).includes(value as ImpactType)
  ) {
    return [value];
  }
  return [];
}
